import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Search, X, Minus, Plus, Trash2, Loader2, Scissors, AlertTriangle } from 'lucide-react'
import { apiGet, postJson } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import { useTypeahead } from '../../lib/useTypeahead.js'
import { relabelBranch } from '../../lib/ticket.js'
import { canAdjustStock } from '../../lib/role.js'
import { useMe } from '../../lib/me.js'
import {
  imageUrl, todayISO, buildStockPayload, fetchInventoryMaster,
  TYPE_CUT, TYPE_ADJUST,
} from '../../lib/stock.js'

// ตัด Stock — the write side, as a side drawer over the history list.
//
// Two panes stacked, both always visible: SEARCH on top (the trigger is "I just
// used X", so finding one item among ~450 is the whole job), and the CART below
// it — the staging list. The cart is the point: nothing is written until you
// press save, so you can add, re-count and remove while standing at the shelf,
// and see exactly what is about to be recorded. One save → N stock rows.
//
// The cart persists across close/refresh: a half-finished count that evaporates
// because someone tapped away is worse than one that lingers.
//
// Type: default เบิกใช้ (a cut — always leaves stock, stored negative). A Stock
// Manager / Admin may switch the whole cart to ปรับ (a correction — either sign,
// any non-zero integer). The sign is decided by the type in lib/stock.js, never
// by what the user types, and re-enforced server-side in functions/api/stock.js.

export default function StockCutDrawer({ branches, defaultBranchId, onClose, onSaved }) {
  const me = useMe()
  const mayAdjust = canAdjustStock(me)
  const searchRef = useRef(null)

  // Branch DEFAULT follows the rule from the parent (main filter, else the user's
  // own branch) rather than a remembered last-used value — so each open lands on
  // the branch you're actually working in. Initialised from the resolved default
  // on mount; the drawer remounts on every open, so this re-applies the rule each
  // time. A manual pick within the drawer sticks until close.
  const [branchId, setBranchId] = useState(() => defaultBranchId ?? null)
  const branchTouched = useRef(false)
  const [type, setType] = usePersistedState('stock:cut:type', TYPE_CUT)
  const [date, setDate] = useState(todayISO)
  const [q, setQ] = useState('')
  const [cart, setCart] = usePersistedState('stock:cut:cart', {})
  const [remark, setRemark] = useState('')

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [progress, setProgress] = useState(null)

  // ปรับ is Stock Manager / Admin only — never leave a non-privileged session
  // stuck on it (e.g. a persisted choice from a shared machine).
  const isAdjust = type === TYPE_ADJUST && mayAdjust
  useEffect(() => {
    if (type === TYPE_ADJUST && !mayAdjust) setType(TYPE_CUT)
  }, [type, mayAdjust, setType])

  const actionWord = isAdjust ? 'ปรับ' : 'ตัด'

  // defaultBranchId can resolve after mount (me / branches load async). Adopt it
  // as long as the user hasn't picked a branch themselves.
  useEffect(() => {
    if (!branchTouched.current && defaultBranchId != null && branchId == null) {
      setBranchId(defaultBranchId)
    }
  }, [defaultBranchId, branchId])

  useEffect(() => { searchRef.current?.focus() }, [])

  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchInventoryMaster(apiGet)
      .then((list) => { if (alive) { setItems(list.filter((i) => i.active !== false)); setError(null) } })
      .catch((e) => { if (alive) setError(e.message || 'โหลดรายการของไม่สำเร็จ') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items])

  // Only show search results once the user actually types — a 450-row dump is
  // not a useful default inside a narrow drawer.
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return []
    return items.filter((i) =>
      String(i.Name || '').toLowerCase().includes(needle) ||
      String(i.subCategory || '').toLowerCase().includes(needle) ||
      String(i.mainCategory || '').toLowerCase().includes(needle)).slice(0, 40)
  }, [items, q])

  // Keep every entry, including a transient 0 in ปรับ mode: for เบิกใช้ the
  // mutators below never leave a 0 or negative behind, so this reads as "qty > 0";
  // for ปรับ a 0 is shown as an invalid line the user must fix, not silently dropped.
  const cartEntries = useMemo(
    () => Object.entries(cart).map(([id, qty]) => ({ id: Number(id), qty: Number(qty) || 0 })),
    [cart])

  const isEntryValid = useCallback(
    (qty) => Number.isInteger(qty) && qty !== 0 && (isAdjust || qty > 0),
    [isAdjust])
  const allValid = cartEntries.length > 0 && cartEntries.every((e) => isEntryValid(e.qty))
  const invalidCount = cartEntries.filter((e) => !isEntryValid(e.qty)).length

  const bump = useCallback((id, delta) => {
    setCart((c) => {
      const next = { ...c }
      const v = (next[id] || 0) + delta
      // เบิกใช้ is a magnitude: hitting 0 removes the line. ปรับ is a signed
      // delta: it may pass through 0 and go negative, so we keep whatever it is.
      if (!isAdjust && v <= 0) delete next[id]
      else next[id] = v
      return next
    })
  }, [setCart, isAdjust])

  const setQty = useCallback((id, raw) => {
    setCart((c) => {
      const next = { ...c }
      if (isAdjust) {
        // Allow a signed integer; a bare '' or '-' mid-typing counts as 0 (an
        // invalid line the user still sees) rather than vanishing.
        const cleaned = String(raw).replace(/[^0-9-]/g, '').replace(/(?!^)-/g, '')
        const n = cleaned === '' || cleaned === '-' ? 0 : Math.trunc(Number(cleaned) || 0)
        next[id] = n
      } else {
        const n = Math.max(0, Math.floor(Number(raw) || 0))
        if (n === 0) delete next[id]
        else next[id] = n
      }
      return next
    })
  }, [setCart, isAdjust])

  const removeItem = useCallback((id) => {
    setCart((c) => { const next = { ...c }; delete next[id]; return next })
  }, [setCart])

  const add = useCallback((id) => {
    bump(id, +1)
    setQ('')
    searchRef.current?.focus()
  }, [bump])

  const typeahead = useTypeahead({
    count: results.length,
    onSelect: (i) => { const it = results[i]; if (it) add(it.id) },
    onClose: () => { setQ(''); searchRef.current?.focus() },
  })

  async function save() {
    setSaving(true)
    setError(null)
    // POST /stock writes ONE row, so a multi-item cut is N requests. Sequential,
    // not parallel: if #3 fails we want #1-2 already durable and an exact "these
    // landed" count, not an ambiguous partial batch.
    const done = []
    try {
      for (const [i, e] of cartEntries.entries()) {
        setProgress({ i: i + 1, n: cartEntries.length })
        await postJson('/api/stock', buildStockPayload({
          type, inventory_id: e.id, branch_id: branchId, date, qty: e.qty, remark,
        }))
        done.push(e.id)
      }
      setCart({})
      setRemark('')
      onSaved?.(done.length, type)
    } catch (err) {
      // Drop what succeeded so a retry cannot double-write them.
      if (done.length) setCart((c) => {
        const next = { ...c }
        for (const id of done) delete next[id]
        return next
      })
      setError(`${err.message || 'บันทึกไม่สำเร็จ'}${done.length ? ` — บันทึกสำเร็จแล้ว ${done.length} รายการ ที่เหลือยังอยู่ในรายการ` : ''}`)
    } finally {
      setSaving(false)
      setProgress(null)
    }
  }

  const canSave = allValid && branchId && !saving

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !saving && onClose?.()} />
      <div className="relative w-full max-w-[560px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in">

        <div className="px-5 py-3.5 bg-white border-b border-slate-200 flex items-start gap-3">
          <span className="w-9 h-9 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
            <Scissors size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[15.5px] font-semibold text-slate-900 leading-tight">ตัด Stock</div>
            <div className="text-[11.5px] text-slate-400 mt-0.5">
              {isAdjust
                ? 'ปรับสต๊อก · ใส่จำนวน + / − · ตรวจรายการ แล้วบันทึกครั้งเดียว'
                : 'ค้นหาของที่ใช้ไป · ใส่จำนวน · ตรวจรายการ แล้วบันทึกครั้งเดียว'}
            </div>
          </div>
          <button onClick={() => !saving && onClose?.()} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={18} />
          </button>
        </div>

        {/* Branch + date (+ type for Stock Manager / Admin) */}
        <div className="px-5 py-3 bg-white border-b border-slate-200 flex flex-wrap gap-3">
          <label className="flex items-center gap-2 text-[13px] text-slate-600">
            <span className="text-slate-400">สาขา</span>
            <select value={branchId ?? ''}
              onChange={(e) => { branchTouched.current = true; setBranchId(Number(e.target.value) || null) }}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800">
              <option value="">— เลือกสาขา —</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>{relabelBranch(b.name || b.Name) || `สาขา ${b.id}`}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-[13px] text-slate-600">
            <span className="text-slate-400">วันที่</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800" />
          </label>
          {mayAdjust && (
            <label className="flex items-center gap-2 text-[13px] text-slate-600">
              <span className="text-slate-400">ประเภท</span>
              <select value={type} onChange={(e) => setType(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[13.5px] text-slate-800">
                <option value={TYPE_CUT}>{TYPE_CUT}</option>
                <option value={TYPE_ADJUST}>{TYPE_ADJUST}</option>
              </select>
            </label>
          )}
        </div>

        {/* Search */}
        <div className="px-5 py-3 bg-white border-b border-slate-200">
          <div className="relative">
            <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={searchRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={typeahead.onKeyDown}
              role="combobox"
              aria-expanded={!!q && results.length > 0}
              aria-controls="stock-cut-results"
              aria-activedescendant={typeahead.activeIndex >= 0 ? `stock-cut-opt-${typeahead.activeIndex}` : undefined}
              placeholder={loading ? 'กำลังโหลดรายการของ…' : 'ค้นหาของที่ใช้ไป…'}
              disabled={loading}
              className="w-full rounded-xl border border-slate-200 bg-white pl-11 pr-10 py-2.5 text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-brand-300 focus:ring-2 focus:ring-brand-100 outline-none disabled:bg-slate-50"
            />
            {loading && <Loader2 size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-slate-300" />}
            {!loading && q && (
              <button onClick={() => { setQ(''); searchRef.current?.focus() }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            )}
          </div>

          {q && (
            <div ref={typeahead.listRef} id="stock-cut-results" role="listbox"
              className="mt-2 max-h-[210px] overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-100">
              {results.length === 0 ? (
                <div className="px-3 py-6 text-center text-[13px] text-slate-400">ไม่พบของที่ตรงกับ “{q}”</div>
              ) : results.map((it, idx) => (
                <button key={it.id} id={`stock-cut-opt-${idx}`} role="option"
                  aria-selected={idx === typeahead.activeIndex}
                  data-ti-index={idx}
                  onMouseEnter={() => typeahead.setActiveIndex(idx)}
                  onClick={() => add(it.id)}
                  className={`w-full px-3 py-2.5 flex items-center gap-3 text-left ${
                    idx === typeahead.activeIndex ? 'bg-brand-50' : 'hover:bg-brand-50/60'}`}>
                  <Thumb item={it} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] text-slate-800 truncate">{it.Name || `#${it.id}`}</div>
                    <div className="text-[11.5px] text-slate-400 truncate">
                      {[it.subCategory, it.unit && `หน่วย: ${it.unit}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  {cart[it.id] != null ? (
                    <span className="text-[11.5px] rounded-full bg-brand-100 text-brand-700 px-2 py-0.5 shrink-0">
                      ในรายการ {cart[it.id]}
                    </span>
                  ) : <Plus size={16} className="text-slate-300 shrink-0" />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* The staging list — inspect + edit before anything is written. */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400">
              รายการที่จะ{actionWord} {cartEntries.length > 0 && `(${cartEntries.length})`}
            </div>
            {cartEntries.length > 0 && (
              <button onClick={() => setCart({})} disabled={saving}
                className="text-[12.5px] text-slate-400 hover:text-rose-600 flex items-center gap-1 disabled:opacity-40">
                <Trash2 size={13} />ล้างทั้งหมด
              </button>
            )}
          </div>

          {cartEntries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white/60 py-10 text-center text-[13px] text-slate-400">
              ยังไม่มีรายการ — ค้นหาของด้านบนเพื่อเพิ่ม
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {cartEntries.map((e) => {
                const it = itemById.get(e.id)
                const bad = !isEntryValid(e.qty)
                return (
                  <div key={e.id} className="px-3 py-2.5 flex items-center gap-3">
                    <Thumb item={it} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] text-slate-800 truncate">{it?.Name || `#${e.id}`}</div>
                      {isAdjust ? (
                        <div className={`text-[11.5px] truncate ${bad ? 'text-amber-600' : e.qty < 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                          {bad ? 'ระบุจำนวน (ไม่ใช่ 0)' : `ปรับ ${e.qty > 0 ? `+${e.qty}` : e.qty}`}
                        </div>
                      ) : it?.subCategory ? (
                        <div className="text-[11.5px] text-slate-400 truncate">{it.subCategory}</div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => bump(e.id, -1)} disabled={saving}
                        className="w-7 h-7 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">
                        <Minus size={13} />
                      </button>
                      <input value={e.qty} onChange={(ev) => setQty(e.id, ev.target.value)}
                        inputMode={isAdjust ? 'text' : 'numeric'} disabled={saving}
                        className={`w-12 h-7 rounded-lg border text-center text-[13.5px] font-semibold ${
                          bad ? 'border-amber-300 text-amber-700' : 'border-slate-200 text-slate-900'}`} />
                      <button onClick={() => bump(e.id, +1)} disabled={saving}
                        className="w-7 h-7 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-40">
                        <Plus size={13} />
                      </button>
                    </div>
                    <span className="w-12 shrink-0 text-[12px] text-slate-400 truncate">{it?.unit || ''}</span>
                    <button onClick={() => removeItem(e.id)} disabled={saving}
                      className="text-slate-300 hover:text-rose-600 shrink-0 disabled:opacity-40">
                      <X size={15} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          <input
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder={`หมายเหตุ (ถ้ามี) — ใช้กับทุกรายการที่${actionWord}ครั้งนี้`}
            disabled={saving}
            className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] placeholder:text-slate-400 outline-none focus:border-brand-300"
          />

          {error && (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex gap-2.5 text-[13px] text-rose-800">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="px-5 py-3.5 bg-white border-t border-slate-200 flex items-center gap-3">
          <button onClick={() => !saving && onClose?.()} disabled={saving}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-[14px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40">
            ปิด
          </button>
          <div className="flex-1 text-[12.5px] text-slate-400">
            {!branchId && cartEntries.length > 0 && <span className="text-amber-600">เลือกสาขาก่อนบันทึก</span>}
            {branchId && invalidCount > 0 && <span className="text-amber-600">มี {invalidCount} รายการที่จำนวนเป็น 0</span>}
            {saving && progress && `กำลังบันทึก ${progress.i}/${progress.n}…`}
          </div>
          <button onClick={save} disabled={!canSave}
            className="rounded-xl bg-brand-600 px-6 py-2.5 text-[14.5px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
            {saving && <Loader2 size={15} className="animate-spin" />}
            บันทึก{actionWord} {cartEntries.length > 0 ? `${cartEntries.length} รายการ` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

function Thumb({ item }) {
  const img = imageUrl(item)
  return (
    <div className="w-9 h-9 rounded-lg bg-slate-50 border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
      {img ? <img src={img} alt="" className="w-full h-full object-cover" />
        : <span className="text-[9px] text-slate-300">ไม่มีรูป</span>}
    </div>
  )
}
